import React from 'react';
import { LayoutDashboard, LineChart, Settings, PanelLeft, PanelLeftClose } from 'lucide-react';
import { useWeatherApi } from '../../context/WeatherApiContext';

type View = 'dashboard' | 'analytics' | 'settings';

interface SidebarProps {
  currentView: View;
  onViewChange: React.Dispatch<React.SetStateAction<View>>;
  isOpen: boolean;
  onToggle: () => void;
  isMobile: boolean;
}

export const Sidebar: React.FC<SidebarProps> = ({ currentView, onViewChange, isOpen, onToggle, isMobile }) => {
  const { status } = useWeatherApi();
  
  const navItems: { id: View; label: string; icon: typeof LayoutDashboard }[] = [
    { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { id: 'analytics', label: 'Analytics', icon: LineChart },
    { id: 'settings', label: 'Settings', icon: Settings },
  ];

  return (
    <aside 
      className={`fixed left-0 top-0 h-full border-r z-50 flex flex-col transition-all duration-300 ${
        isMobile
          ? isOpen
            ? 'translate-x-0'
            : '-translate-x-full'
          : ''
      }`}
      style={{
        backgroundColor: 'rgba(28, 28, 30, 0.8)',
        borderColor: 'rgba(255, 255, 255, 0.1)',
        backdropFilter: 'blur(30px)',
        width: isMobile ? '16rem' : isOpen ? '16rem' : '4rem',
      }}
    >
      <div
        className="border-b border-white/10 flex items-center"
        style={{
          padding: isOpen ? '1rem' : '1rem 0.5rem 1rem 0.5rem',
          justifyContent: isOpen ? 'space-between' : 'center',
          gap: isOpen ? '0.5rem' : '0.25rem',
        }}
      >
        <div className="flex items-center gap-2">
          {isOpen && <img src="/Icon.svg" alt="Logo" className="w-10 h-10" />}
          {isOpen && <img src="/Brand Name.svg" alt="Brand" className="h-11" />}
        </div>
        <button
          type="button"
          onClick={onToggle}
          className={`p-2 rounded-lg border border-white/10 bg-white/5 hover:bg-white/10 transition-colors ${
            isOpen ? '' : 'w-10 h-10 flex items-center justify-center'
          }`}
          aria-label={isOpen ? 'Collapse sidebar' : 'Expand sidebar'}
        >
          {isOpen ? <PanelLeftClose size={18} /> : <PanelLeft size={18} />}
        </button>
      </div>
      <nav className="p-3 flex-1 space-y-2">
        {navItems.map((item) => {
          const Icon = item.icon;
          const isActive = currentView === item.id;
          return (
            <button
              key={item.id}
              onClick={() => onViewChange(item.id)}
              className={`w-full flex items-center ${
                isOpen ? 'justify-start gap-3 px-4 py-3' : 'justify-center py-3'
              } rounded-xl transition-all duration-300 ${
                isActive
                  ? 'bg-ios-blue/15 text-ios-blue font-semibold'
                  : 'text-gray-400 hover:text-white hover:bg-white/5'
              }`}
            >
              <Icon size={20} />
              {isOpen && <span>{item.label}</span>}
            </button>
          );
        })}
      </nav>
      <div className="p-4 border-t border-white/10">
        <div className={`flex items-center ${isOpen ? 'gap-2 text-sm' : 'justify-center'}`}>
          <div
            className={`w-2 h-2 rounded-full ${
              status.connected ? 'bg-ios-green' : 'bg-gray-500'
            }`}
          />
          {isOpen && (
            <span className={status.connected ? 'text-ios-green' : 'text-gray-400'}>
              {status.connected ? 'Online' : 'Offline'}
            </span>
          )}
        </div>
      </div>
    </aside>
  );
};
